import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ListItemAvatar,
  Avatar,
  IconButton,
  Box,
  Typography,
  TextField,
  MenuItem,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import professionalsService from '../../services/professionalsService';
import placeProfessionalsService from '../../services/placeProfessionalsService';
import { handleDeleteOperation } from '../../utils/apiErrorHandler';
import useSnackbar from '../../hooks/useSnackbar';
import { getInitials } from '../../utils/professionalNameUtils';
import ProfessionalAvatar from '../ProfessionalAvatar';

/**
 * PlaceProfessionalsManager - Gerenciador de Profissionais do Local
 * 
 * Permite adicionar e remover profissionais que atendem em um local específico.
 * 
 * @param {Boolean} open - Se o diálogo está aberto
 * @param {Function} onClose - Callback ao fechar
 * @param {Object} place - Local sendo gerenciado
 * @param {Function} onUpdate - Callback após atualização (para recarregar dados)
 */
export default function PlaceProfessionalsManager({ open, onClose, place, onUpdate }) {
  const [allProfessionals, setAllProfessionals] = useState([]);
  const [placeProfessionals, setPlaceProfessionals] = useState([]);
  const [selectedProfessionalId, setSelectedProfessionalId] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingProfessionals, setLoadingProfessionals] = useState(false);
  const { showSuccess, showError } = useSnackbar();

  // Carregar todos os profissionais disponíveis
  useEffect(() => {
    if (open) {
      loadAllProfessionals();
      loadPlaceProfessionals();
    }
  }, [open, place?.id]);

  const loadAllProfessionals = async () => {
    try {
      setLoadingProfessionals(true);
      const response = await professionalsService.getAll();
      // API retorna estrutura normalizada: { professionals: [], places: [], services: [], payments: [] }
      const professionals = response?.professionals || response || [];
      setAllProfessionals(professionals);
    } catch (error) {
      showError('Erro ao carregar profissionais: ' + (error.response?.data?.message || error.message));
      setAllProfessionals([]);
    } finally {
      setLoadingProfessionals(false);
    }
  };

  const loadPlaceProfessionals = async () => {
    if (!place?.id) return;
    
    try {
      setLoading(true);
      const placeProfessionals = await placeProfessionalsService.getByPlaceId(place.id);
      setPlaceProfessionals(placeProfessionals || []);
    } catch (error) {
      console.error('Erro ao carregar profissionais do local:', error);
      setPlaceProfessionals([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddProfessional = async () => {
    if (!selectedProfessionalId || !place?.id) return;

    // Verificar se o profissional já está adicionado
    const alreadyAdded = placeProfessionals.some(pp => pp.professionalId === selectedProfessionalId);
    if (alreadyAdded) {
      showError('Este profissional já está vinculado a este local');
      return;
    }

    try {
      setLoading(true);
      await placeProfessionalsService.create(place.id, {
        professionalId: selectedProfessionalId,
      });
      
      showSuccess('Profissional vinculado com sucesso!');
      setSelectedProfessionalId('');
      await loadPlaceProfessionals();
      onUpdate?.();
    } catch (error) {
      showError('Erro ao vincular profissional: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveProfessional = async (placeProfessionalId) => {
    if (!window.confirm('Tem certeza que deseja desvincular este profissional do local?')) {
      return;
    }

    try {
      setLoading(true);
      
      // ✅ Usar handleDeleteOperation para tratar respostas corretamente
      const result = await handleDeleteOperation(
        (params) => placeProfessionalsService.delete(params.placeId, params.id),
        { placeId: place.id, id: placeProfessionalId }
      );

      if (result.success) {
        // Sucesso (HTTP 204)
        showSuccess('Profissional desvinculado com sucesso!');
        await loadPlaceProfessionals();
        onUpdate?.();
      } else {
        // Erro (400, 404, 500, etc)
        const errorMsg = result.error.message;
        showError(errorMsg);
      }
    } catch (error) {
      // Fallback para erros inesperados
      showError('Erro ao desvincular profissional: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedProfessionalId('');
    onClose();
  };

  // Filtrar profissionais disponíveis (que ainda não foram adicionados)
  const availableProfessionals = allProfessionals.filter(
    prof => !placeProfessionals.some(pp => pp.professionalId === prof.id)
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PersonIcon />
          <Typography variant="h6">
            Gerenciar Profissionais - {place?.name}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Adicionar Novo Profissional */}
        <Box mb={3}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Vincular Profissional:
          </Typography>
          <Box display="flex" gap={1} alignItems="flex-start">
            <TextField
              select
              fullWidth
              label="Selecione um profissional"
              value={selectedProfessionalId}
              onChange={(e) => setSelectedProfessionalId(e.target.value)}
              disabled={loading || loadingProfessionals}
              size="small"
            >
              {loadingProfessionals ? (
                <MenuItem disabled>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Carregando...
                </MenuItem>
              ) : availableProfessionals.length === 0 ? (
                <MenuItem disabled>Todos os profissionais já foram vinculados</MenuItem>
              ) : (
                availableProfessionals.map((prof) => (
                  <MenuItem key={prof.id} value={prof.id}>
                    {prof.personName || prof.name || 'Profissional'}
                    {prof.specialization && ` - ${prof.specialization}`}
                  </MenuItem>
                ))
              )}
            </TextField>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddProfessional}
              disabled={!selectedProfessionalId || loading}
            >
              Adicionar
            </Button>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Lista de Profissionais do Local */}
        <Typography variant="subtitle2" gutterBottom fontWeight="bold">
          Profissionais Vinculados ({placeProfessionals.length}):
        </Typography>

        {loading && placeProfessionals.length === 0 ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress />
          </Box>
        ) : placeProfessionals.length === 0 ? (
          <Alert severity="info">
            Nenhum profissional vinculado a este local. Adicione profissionais usando o campo acima.
          </Alert>
        ) : (
          <List>
            {placeProfessionals.map((placeProfessional) => {
              const initials = getInitials(placeProfessional.professionalName || 'Profissional');

              return (
                <ListItem
                  key={placeProfessional.id}
                  divider
                  sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    mb: 1,
                  }}
                >
                  <ListItemAvatar>
                    <ProfessionalAvatar
                      professionalId={placeProfessional.professionalId}
                      photoTimestamp={placeProfessional.photoTimestamp}
                      name={placeProfessional.professionalName}
                      sx={{ bgcolor: 'primary.main' }}
                    >
                      {initials}
                    </ProfessionalAvatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={placeProfessional.professionalName || 'Profissional'}
                    secondary={
                      <>
                        {placeProfessional.specialization && (
                          <Chip
                            label={placeProfessional.specialization}
                            size="small"
                            sx={{ mt: 0.5, mr: 0.5 }}
                          />
                        )}
                        {placeProfessional.professionalEmail && (
                          <Typography variant="body2" color="text.secondary" component="span">
                            {placeProfessional.professionalEmail}
                          </Typography>
                        )}
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      color="error"
                      onClick={() => handleRemoveProfessional(placeProfessional.id)}
                      disabled={loading}
                      title="Desvincular profissional"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        )}

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="caption">
            <strong>Dica:</strong> Os profissionais vinculados poderão atender neste local. 
            Configure os serviços que cada profissional oferece usando o gerenciador de serviços.
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
